import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { SharedEditableInputComponent } from '../shared-input.component';
import { FeedbackService } from '../../../../../core/feedback.service';
import { FormArray, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { copyContentToClipboard } from '../../../../../utils/miscellaneous';
import { MatInput } from '@angular/material/input';
import { EditLocationService } from '../../service/edit-location.service';
import { memoize } from '../../../../../core/decorators/memoize';
import { EditLocationLocalStore } from '../../edit-location-local-store.service';
import { LSCField } from '../../../../../models/LSCFields';
import { CustomServicesTControl } from '../../../../../models/locations-models/edit-location-form';
import { getCustomServiceAsString } from '../../../location-list/location-list-utils';
import { CustomService } from '../../../../../models/locations-models/location';
import { NgStyle } from '@angular/common';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIconButton } from '@angular/material/button';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { MatIcon } from '@angular/material/icon';
import { GxInputDirective } from '../../../../../directives/gxinput.directive';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';

@Component({
    selector: 'app-editable-custom-services-inputs',
    templateUrl: './editable-custom-services-inputs.component.html',
    styleUrls: ['./editable-custom-services-inputs.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        MatFormField,
        MatLabel,
        MatInput,
        GxInputDirective,
        MatIcon,
        MatError,
        CdkTextareaAutosize,
        MatIconButton,
        MatTooltip,
        NgStyle,
    ],
})
export class EditableCustomServicesInputsComponent extends SharedEditableInputComponent implements OnInit {

  @Input() subType:
      | 'text'
      | 'multiline_text'
      = 'text';

  @Input() formGroupNameWithVisibleButtons: number | null;
  // this time, the group name ref is a numeric index of the `formArray` control
  @Input() formGroupNameRef: number;

  // will be focus()-ed on-demand in the parent section right after
  // a new custom serv. group is added
  @ViewChild('serviceNameInput') serviceNameInput: ElementRef<MatInput>;

  // the `formGroupNameRef` in case of custom services is the array
  // index of a given service name/desc object
  private get serviceArrayIndex(): number {
    return this.formGroupNameRef;
  }

  constructor(protected cd: ChangeDetectorRef,
              protected feedbackService: FeedbackService,
              protected sidePanelService: EditLocationService,
              protected localStore: EditLocationLocalStore) {
    super(cd, feedbackService, sidePanelService, localStore);
  }

  ngOnInit(): void {
    super.ngOnInit();
  }

  deleteCustomService() {
    this.customServicesFormArray.removeAt(this.serviceArrayIndex);
  }

  private get customServicesFormArray() {
    return this.localStore.formGroup?.get(LSCField.customServices) as FormArray<FormGroup<CustomServicesTControl>>;
  }

  @memoize()
  areActionButtonsVisible() {
    return this.formGroupNameWithVisibleButtons !== null &&
        this.formGroupNameWithVisibleButtons === this.serviceArrayIndex;
  }

  /**
   * Copy both, service name and description (if available) to clipboard
   * @param $event the click event
   */
  copyValue($event: MouseEvent) {
    $event.stopPropagation();

    // Create a dummy input to copy the string inside it, set it's ID
    const dummyInputElement = document.createElement('input');
    const dummyInputElementId = 'dummy_input_element';
    document.body.appendChild(dummyInputElement);
    dummyInputElement.setAttribute('id', dummyInputElementId);

    // get formGroup value 
    const formGroupValue = this.customServicesFormArray.controls[this.serviceArrayIndex].value;
    // set dummy input's value 
    (document.getElementById(dummyInputElementId) as HTMLInputElement).value =
        getCustomServiceAsString(formGroupValue as CustomService);

    // attempt to copy dummy input's value and remove dummy input from the DOM
    copyContentToClipboard(
        dummyInputElement,
        (ok) => {
          document.body.removeChild(dummyInputElement);
          return ok && this.feedbackService.showSuccessSnackbar({
            data: { userFriendlyMessage: 'Copied!' },
            isAddressListSnackbar: true,
            duration: 3000,
          });
        });
  }

  /**
   * Extract the error message based on the current index.
   */
  getIndexAwareErrorMessage(): string | null {
    return super.getErrorMessage(this.serviceArrayIndex);
  }

  /**
   * Field-specific checks.
   * @param fieldId
   */
  hasFieldBeenEdited(fieldId: 'serviceName' | 'serviceDescription' | 'serviceCategoryId') {
    return super.hasBeenEdited(this.serviceArrayIndex, fieldId);
  }

}
